import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from './contexts/CustomerAuthContext';
import {
  ArrowLeft, Package, Truck, MapPin, Phone, RotateCcw, X, AlertCircle,
  ShoppingBag, ExternalLink, XCircle
} from 'lucide-react';
import AIOrderTracking from './components/AIOrderTracking';
import './styles/order-details.css';

const RETURN_REASONS = [
  { value: 'DEFECTIVE',        label: 'Item is defective' },
  { value: 'WRONG_ITEM',       label: 'Wrong item received' },
  { value: 'NOT_AS_DESCRIBED', label: 'Item not as described' },
  { value: 'CHANGED_MIND',     label: 'Changed my mind' },
  { value: 'SIZE_FIT_ISSUE',   label: 'Size/fit issue' },
  { value: 'QUALITY_ISSUE',    label: 'Quality issue' },
  { value: 'ARRIVED_LATE',     label: 'Arrived too late' },
  { value: 'OTHER',            label: 'Other reason' }
];

const RETURN_STATUS_LABEL = {
  PENDING:            'Pending review',
  MERCHANT_APPROVED:  'Approved',
  MERCHANT_REJECTED:  'Rejected',
  RESOLVED:           'Resolved',
  CANCELLED:          'Cancelled'
};

const RETURN_STATUS_CLASS = {
  PENDING:           'odet-pill odet-pill--pending',
  MERCHANT_APPROVED: 'odet-pill odet-pill--shipped',
  MERCHANT_REJECTED: 'odet-pill odet-pill--cancelled',
  RESOLVED:          'odet-pill odet-pill--delivered',
  CANCELLED:         'odet-pill odet-pill--cancelled'
};

const pillClass = (status) => {
  const key = (status || 'pending').toLowerCase();
  return `odet-pill odet-pill--${key}`;
};

const formatPrice = (value) => new Intl.NumberFormat('en-NG', {
  style: 'currency', currency: 'NGN', minimumFractionDigits: 2, maximumFractionDigits: 2
}).format(parseFloat(value) || 0);

const formatDateTime = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const OrderDetails = () => {
  const { orderId } = useParams();
  const { isAuthenticated } = useCustomerAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [returnRequest, setReturnRequest] = useState(null);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [returnNotes, setReturnNotes] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [submittingReturn, setSubmittingReturn] = useState(false);
  const [returnError, setReturnError] = useState('');
  const [actionBusy, setActionBusy] = useState(false);

  const fetchOrderDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/customer/order-tracking/${orderId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setOrder(data.order);
      } else {
        setError('Order not found');
      }
    } catch (err) {
      setError('Failed to fetch order details');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const checkReturnRequest = useCallback(async () => {
    try {
      const response = await fetch(`/api/customer/return-requests/order/${orderId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.hasReturnRequest) setReturnRequest(data.returnRequest);
      }
    } catch (err) {
      /* non-blocking */
    }
  }, [orderId]);

  useEffect(() => {
    if (orderId && isAuthenticated) {
      fetchOrderDetails();
      checkReturnRequest();
    }
  }, [orderId, isAuthenticated, fetchOrderDetails, checkReturnRequest]);

  const handleSubmitReturn = async () => {
    if (!returnReason) { setReturnError('Please select a reason for the return'); return; }
    if (selectedItems.length === 0) { setReturnError('Please select at least one item to return'); return; }
    try {
      setSubmittingReturn(true);
      setReturnError('');
      const response = await fetch('/api/customer/return-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          orderId: order.id || order._id,
          items: selectedItems.map(item => ({
            productId:   item.productId || item.product?.id,
            productName: item.name      || item.product?.name,
            qty:         item.qty       || item.quantity || 1
          })),
          reason:        returnReason,
          customerNotes: returnNotes || undefined
        })
      });
      const data = await response.json();
      if (response.ok) {
        setReturnRequest(data.returnRequest);
        setShowReturnModal(false);
        setReturnReason('');
        setReturnNotes('');
        setSelectedItems([]);
      } else {
        setReturnError(data.error || 'Failed to submit return request');
      }
    } catch (err) {
      setReturnError('Failed to submit return request');
    } finally {
      setSubmittingReturn(false);
    }
  };

  const handleCancelReturn = async () => {
    if (!returnRequest) return;
    try {
      const response = await fetch(`/api/customer/return-requests/${returnRequest.id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (response.ok) setReturnRequest(null);
    } catch (err) {
      /* non-blocking */
    }
  };

  const toggleItemSelection = (item) => {
    const itemId = item.productId || item.product?.id;
    const isSelected = selectedItems.some(i => (i.productId || i.product?.id) === itemId);
    if (isSelected) {
      setSelectedItems(selectedItems.filter(i => (i.productId || i.product?.id) !== itemId));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const handleCancelOrder = async () => {
    if (!order) return;
    try {
      setActionBusy(true);
      const resp = await fetch(`/api/customer/order-tracking/${order.id || order._id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: 'Customer requested cancellation' })
      });
      if (resp.ok) await fetchOrderDetails();
    } catch (e) {
      /* non-blocking */
    } finally {
      setActionBusy(false);
    }
  };

  const handleReorder = async () => {
    if (!order) return;
    try {
      setActionBusy(true);
      const resp = await fetch(`/api/customer/order-tracking/${order.id || order._id}/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (resp.ok) navigate('/dashboard/cart');
    } catch (e) {
      /* non-blocking */
    } finally {
      setActionBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="odet">
        <div className="odet__skel odet__skel--head" />
        <div className="odet__skel odet__skel--block" />
        <div className="odet__skel odet__skel--block" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="odet">
        <div className="odet__empty">
          <XCircle size={28} aria-hidden />
          <div>
            <div className="odet__empty-title">Order not found</div>
            <div className="odet__empty-sub">{error || "The order you're looking for doesn't exist."}</div>
          </div>
          <button type="button" className="odet__empty-btn" onClick={() => navigate('/dashboard/orders')}>
            <ArrowLeft size={14} /> Back to orders
          </button>
        </div>
      </div>
    );
  }

  const orderNumber = order.orderNumber || (order.id ? order.id.slice(-8).toUpperCase() : order._id);
  const status = (order.status || '').toLowerCase();
  const items = order.items || order.orderItems || [];
  const canCancel  = ['pending', 'processing'].includes(status);
  const canReturn  = status === 'delivered' && !returnRequest;
  const canReorder = !canCancel && !canReturn;
  const rider = order.rider || order.deliveryTracking?.rider || null;
  const trackingUrl = order.rider?.trackingUrl
    || order.deliveryTracking?.rider?.trackingUrl
    || order.deliveryTracking?.trackingUrl
    || null;
  const trackingCode = rider?.trackingCode || null;
  const isChowdeck = order.deliveryTracking?.method === 'CHOWDECK';

  return (
    <div className="odet">
      {/* Back link */}
      <button type="button" className="odet__back" onClick={() => navigate('/dashboard/orders')}>
        <ArrowLeft size={14} aria-hidden /> Back to orders
      </button>

      {/* Order header */}
      <header className="odet__head">
        <div className="odet__head-text">
          <h1 className="odet__title">Order #{orderNumber}</h1>
          <p className="odet__meta">Placed {formatDateTime(order.createdAt)}</p>
        </div>
        <span className={pillClass(order.status)}>{order.status || 'Pending'}</span>
      </header>

      {/* AI tracking */}
      <AIOrderTracking orderId={order.id || order._id} orderStatus={order.status} />

      {/* Items */}
      <section className="odet__card" aria-label="Order items">
        <header className="odet__card-head">
          <h2 className="odet__card-title">
            <Package size={14} aria-hidden /> Items ({items.length})
          </h2>
        </header>
        <ul className="odet-items">
          {items.map((item, idx) => {
            const itemImage =
              item.product?.primaryImage ||
              (item.product?.images && item.product.images[0]) ||
              item.productImage ||
              null;
            const name = item.name || item.product?.name || 'Product';
            const qty = item.qty || item.quantity || 1;
            const linePrice = (parseFloat(item.price) || 0) * qty;
            return (
              <li key={idx} className="odet-items__row">
                <div className="odet-items__img">
                  {itemImage
                    ? <img src={itemImage} alt={name} onError={(e) => { e.target.style.display = 'none'; }} />
                    : <Package size={20} aria-hidden />}
                </div>
                <div className="odet-items__body">
                  <div className="odet-items__name">{name}</div>
                  <div className="odet-items__meta">Qty {qty}</div>
                </div>
                <div className="odet-items__price">{formatPrice(linePrice)}</div>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Summary */}
      <section className="odet__card" aria-label="Order summary">
        <header className="odet__card-head">
          <h2 className="odet__card-title">Summary</h2>
        </header>
        <dl className="odet-sum">
          <div className="odet-sum__row">
            <dt>Subtotal</dt>
            <dd>{formatPrice(order.subtotal || 0)}</dd>
          </div>
          {order.shippingFee > 0 && (
            <div className="odet-sum__row">
              <dt>Shipping</dt>
              <dd>{formatPrice(order.shippingFee)}</dd>
            </div>
          )}
          {(order.tax > 0 || order.taxTotal > 0) && (
            <div className="odet-sum__row">
              <dt>Tax</dt>
              <dd>{formatPrice(order.tax || order.taxTotal)}</dd>
            </div>
          )}
          {order.serviceCharge > 0 && (
            <div className="odet-sum__row">
              <dt>Service charge</dt>
              <dd>{formatPrice(order.serviceCharge)}</dd>
            </div>
          )}
          <div className="odet-sum__row odet-sum__row--total">
            <dt>Total</dt>
            <dd>{formatPrice(order.total || 0)}</dd>
          </div>
        </dl>
      </section>

      {/* Shipping info */}
      {order.shippingInfo && (
        <section className="odet__card" aria-label="Shipping information">
          <header className="odet__card-head">
            <h2 className="odet__card-title">
              <MapPin size={14} aria-hidden /> Shipping
            </h2>
          </header>
          <dl className="odet-info">
            <div className="odet-info__row">
              <dt>Recipient</dt>
              <dd>{order.shippingInfo.firstName} {order.shippingInfo.lastName}</dd>
            </div>
            {order.shippingInfo.phone && (
              <div className="odet-info__row">
                <dt>Phone</dt>
                <dd>
                  <a href={`tel:${order.shippingInfo.phone}`} className="odet-info__link">
                    <Phone size={12} aria-hidden /> {order.shippingInfo.phone}
                  </a>
                </dd>
              </div>
            )}
            <div className="odet-info__row">
              <dt>Address</dt>
              <dd>
                {order.shippingInfo.address}<br />
                {order.shippingInfo.city}{order.shippingInfo.state && `, ${order.shippingInfo.state}`} {order.shippingInfo.zipCode}<br />
                {order.shippingInfo.country}
              </dd>
            </div>
          </dl>
        </section>
      )}

      {/* Delivery tracking */}
      {(rider?.name || isChowdeck) && (
        <section className="odet__card" aria-label="Delivery">
          <header className="odet__card-head">
            <h2 className="odet__card-title">
              <Truck size={14} aria-hidden />
              {isChowdeck ? 'Chowdeck express' : 'Delivery'}
            </h2>
          </header>

          {rider?.name ? (
            <div className="odet-rider">
              <div className="odet-rider__row">
                <div className="odet-rider__name">{rider.name}</div>
                {(rider.company || order.deliveryTracking?.provider) && (
                  <span className="odet-rider__company">
                    {rider.company || order.deliveryTracking?.provider}
                  </span>
                )}
              </div>
              {rider.phone && (
                <a href={`tel:${rider.phone}`} className="odet-rider__phone">
                  <Phone size={14} aria-hidden /> {rider.phone}
                </a>
              )}
              {trackingCode && (
                <div className="odet-rider__track">
                  <div>
                    <div className="odet-rider__track-label">Tracking code</div>
                    <div className="odet-rider__track-code">{trackingCode}</div>
                  </div>
                  {trackingUrl && (
                    <a href={trackingUrl} target="_blank" rel="noopener noreferrer" className="odet-rider__track-btn">
                      Track package <ExternalLink size={12} aria-hidden />
                    </a>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="odet-rider odet-rider--pending">
              <span className="odet-pill odet-pill--processing">
                {order.deliveryTracking?.status || 'Processing'}
              </span>
              <span className="odet-rider__pending-text">Chowdeck rider will be assigned soon</span>
            </div>
          )}
        </section>
      )}

      {/* Store contact */}
      {order.merchantId && (order.merchantId.name || order.merchantId.contactInfo) && (
        <section className="odet__card" aria-label="Store contact">
          <header className="odet__card-head">
            <h2 className="odet__card-title">Store</h2>
          </header>
          <dl className="odet-info">
            {(order.merchantId.name || order.merchantId.slug) && (
              <div className="odet-info__row">
                <dt>Name</dt>
                <dd>{order.merchantId.name || order.merchantId.slug}</dd>
              </div>
            )}
            {order.merchantId.contactInfo?.email && (
              <div className="odet-info__row">
                <dt>Email</dt>
                <dd>{order.merchantId.contactInfo.email}</dd>
              </div>
            )}
            {order.merchantId.contactInfo?.phone && (
              <div className="odet-info__row">
                <dt>Phone</dt>
                <dd>{order.merchantId.contactInfo.phone}</dd>
              </div>
            )}
          </dl>
        </section>
      )}

      {/* Return request */}
      {returnRequest && (
        <section className="odet__card" aria-label="Return request">
          <header className="odet__card-head">
            <h2 className="odet__card-title">
              <RotateCcw size={14} aria-hidden /> Return request
            </h2>
            <span className={RETURN_STATUS_CLASS[returnRequest.status] || 'odet-pill odet-pill--pending'}>
              {RETURN_STATUS_LABEL[returnRequest.status] || returnRequest.status}
            </span>
          </header>
          <dl className="odet-info">
            <div className="odet-info__row">
              <dt>Request #</dt>
              <dd>{returnRequest.requestNumber}</dd>
            </div>
            <div className="odet-info__row">
              <dt>Reason</dt>
              <dd>{RETURN_REASONS.find(r => r.value === returnRequest.reason)?.label || returnRequest.reason}</dd>
            </div>
            {returnRequest.customerNotes && (
              <div className="odet-info__row">
                <dt>Your notes</dt>
                <dd>{returnRequest.customerNotes}</dd>
              </div>
            )}
            {returnRequest.merchantNotes && (
              <div className="odet-info__row">
                <dt>Merchant response</dt>
                <dd>{returnRequest.merchantNotes}</dd>
              </div>
            )}
            {returnRequest.resolution && (
              <div className="odet-info__row">
                <dt>Resolution</dt>
                <dd>{returnRequest.resolution.replace(/_/g, ' ')}</dd>
              </div>
            )}
            <div className="odet-info__row">
              <dt>Submitted</dt>
              <dd>{new Date(returnRequest.createdAt).toLocaleDateString()}</dd>
            </div>
          </dl>
          {returnRequest.status === 'PENDING' && (
            <div className="odet-actions">
              <button type="button" className="odet-btn odet-btn--ghost" onClick={handleCancelReturn}>
                Cancel request
              </button>
            </div>
          )}
        </section>
      )}

      {/* Sticky action bar */}
      <div className="odet-actions odet-actions--bar">
        {canCancel && (
          <button type="button" className="odet-btn odet-btn--danger" onClick={handleCancelOrder} disabled={actionBusy}>
            Cancel order
          </button>
        )}
        {canReturn && (
          <button
            type="button"
            className="odet-btn odet-btn--ghost"
            onClick={() => {
              setSelectedItems([]);
              setReturnReason('');
              setReturnNotes('');
              setReturnError('');
              setShowReturnModal(true);
            }}
          >
            <RotateCcw size={14} aria-hidden /> Request return
          </button>
        )}
        {canReorder && (
          <button type="button" className="odet-btn odet-btn--primary" onClick={handleReorder} disabled={actionBusy}>
            <ShoppingBag size={14} aria-hidden /> Reorder
          </button>
        )}
      </div>

      {/* Return modal */}
      {showReturnModal && (
        <div className="odet-modal" role="dialog" aria-modal="true" onMouseDown={() => setShowReturnModal(false)}>
          <div className="odet-modal__panel" onMouseDown={(e) => e.stopPropagation()}>
            <header className="odet-modal__head">
              <h2 className="odet-modal__title">Request return</h2>
              <button type="button" className="odet-modal__close" onClick={() => setShowReturnModal(false)} aria-label="Close">
                <X size={16} aria-hidden />
              </button>
            </header>

            <div className="odet-modal__body">
              {returnError && (
                <div className="odet__alert" role="alert">
                  <AlertCircle size={14} aria-hidden /> <span>{returnError}</span>
                </div>
              )}

              <div className="odet-modal__section">
                <h3 className="odet-modal__section-title">Items to return</h3>
                <ul className="odet-ret-items">
                  {items.map((item, idx) => {
                    const itemId = item.productId || item.product?.id;
                    const isSelected = selectedItems.some(i => (i.productId || i.product?.id) === itemId);
                    return (
                      <li
                        key={idx}
                        className={`odet-ret-item ${isSelected ? 'is-selected' : ''}`}
                        onClick={() => toggleItemSelection(item)}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleItemSelection(item)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="odet-ret-item__body">
                          <div className="odet-ret-item__name">{item.name || item.product?.name}</div>
                          <div className="odet-ret-item__meta">Qty {item.qty || item.quantity || 1}</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="odet-modal__section">
                <h3 className="odet-modal__section-title">Reason</h3>
                <select
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                  className="odet-input"
                >
                  <option value="">Select a reason…</option>
                  {RETURN_REASONS.map(reason => (
                    <option key={reason.value} value={reason.value}>{reason.label}</option>
                  ))}
                </select>
              </div>

              <div className="odet-modal__section">
                <h3 className="odet-modal__section-title">Additional notes (optional)</h3>
                <textarea
                  value={returnNotes}
                  onChange={(e) => setReturnNotes(e.target.value)}
                  placeholder="Anything else the merchant should know?"
                  className="odet-input"
                  rows={3}
                />
              </div>
            </div>

            <footer className="odet-modal__foot">
              <button
                type="button"
                className="odet-btn odet-btn--ghost"
                onClick={() => setShowReturnModal(false)}
                disabled={submittingReturn}
              >
                Cancel
              </button>
              <button
                type="button"
                className="odet-btn odet-btn--primary"
                onClick={handleSubmitReturn}
                disabled={submittingReturn || !returnReason || selectedItems.length === 0}
              >
                {submittingReturn ? 'Submitting…' : 'Submit request'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetails;
